import React from "react";
import dayjs from "dayjs";
import { useDispatch } from "../redux/store";
import { updateAppointment } from "../redux/slices/appointmentSlice";
import { AppDispatch } from "../redux/store";
import { AppointmentState } from "../types";
import { ApptType } from "../types/booking-config.interface";

const TRANSPORT_TYPES = [
  { flag: "shuttleEnabled", label: "Shuttle" },
  { flag: "loanerEnabled", label: "Loaner" },
  { flag: "rentalEnabled", label: "Rental Car" },
] as const;

interface AppointmentSummaryProps {
  appointment: AppointmentState;
  selectedDate: string | null;
  selectedTime: string;
  stayOrDropOff: ApptType;
  setClear: (clear: boolean) => void;
  onEdit?: () => void;
}

export const AppointmentSummary: React.FC<AppointmentSummaryProps> = ({
  appointment,
  selectedDate,
  selectedTime,
  stayOrDropOff,
  setClear,
  onEdit,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const accentColor = appointment?.shopData?.color || "#2b73b9";

  const formatTimeForCustomer = (timeString: string): string => {
    if (timeString.includes(" - ")) {
      return timeString.split(" - ")[0];
    }
    return timeString;
  };

  const onCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
    if (e.target.value !== "" || !appointment.shopData?.requireComments) {
      dispatch(updateAppointment({ updates: { comments: e.target.value } }));
      setClear(true);
    } else {
      dispatch(updateAppointment({ updates: { comments: "" } }));
      setClear(false);
    }
  };

  const serviceList = appointment?.services?.length
    ? appointment.services.map((svc) => svc.name)
    : appointment?.service
    ? [appointment.service]
    : [];
  const serviceDescription =
    serviceList.length > 1 ? serviceList.join(" and ") : serviceList[0] || "";
  const hasAnyTransportOption = TRANSPORT_TYPES.some(
    (transport) =>
      appointment?.shopData?.[
        transport.flag as keyof NonNullable<AppointmentState["shopData"]>
      ]
  );
  const transportPreference =
    appointment?.complimentaryRide?.trim() || "Not selected";

  return (
    <div
      id="appointment-summary"
      className="rounded-2xl bg-white p-5 font-roboto min-w-0 overflow-hidden"
      style={{
        border: `2px solid ${accentColor}33`,
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      }}
    >
      <h3
        className="text-base font-bold text-center mb-4"
        style={{ color: "#1f2937" }}
      >
        Appointment Summary
      </h3>

      <div className="text-sm text-gray-700 mb-3">
        {`You've chosen to ${
          stayOrDropOff.toLowerCase() === "wait"
            ? "wait with your vehicle during your"
            : "drop-off your vehicle for your"
        } `}
        <strong>{serviceDescription}</strong>
        {" service."}
        {appointment?.value === "9" &&
          " Please describe what we need to inspect in the addition comments box below."}
      </div>
      <div className="text-sm text-gray-700 mb-4">
        The date and time of your service will be{" "}
        <strong>
          {selectedDate && dayjs(selectedDate).format("MM/DD/YYYY")} at{" "}
          {formatTimeForCustomer(selectedTime)}
        </strong>
      </div>
      {hasAnyTransportOption && stayOrDropOff === "drop" && (
        <div className="text-sm text-gray-700 mb-4">
          Transportation preference: <strong>{transportPreference}</strong>
        </div>
      )}
      <div>
        <div className="text-sm font-semibold text-gray-800 mb-1">
          Additional Comments
          {appointment.shopData?.requireComments && (
            <span style={{ color: "#ef4444" }}> - required</span>
          )}
        </div>
        <textarea
          className="w-full max-w-full rounded-lg p-3 text-sm font-roboto outline-none focus:outline-none resize-y"
          rows={4}
          placeholder="Enter any additional comments here..."
          onChange={onCommentChange}
          style={{
            color: "#1f2937",
            backgroundColor: "#ffffff",
            border: `2px solid ${accentColor}44`,
            boxSizing: "border-box",
            boxShadow: "none",
          }}
          defaultValue={appointment?.comments || ""}
        />
      </div>

      {onEdit && (
        <div className="mt-4 text-center">
          <button
            type="button"
            className="inline-flex items-center gap-1.5 text-sm font-semibold transition-opacity hover:opacity-80 focus:outline-none cursor-pointer font-roboto"
            style={{ color: accentColor, background: "none", border: "none" }}
            onClick={onEdit}
          >
            <i className="fa-solid fa-pen-to-square" aria-hidden="true" style={{ fontSize: "13px" }} />
            Edit Appointment
          </button>
        </div>
      )}
    </div>
  );
};
